import Vue from 'vue'
import axios from 'axios'
import store from '@/store'
import i18n from '@/utils/i18n'
import { showToast } from '@/utils/toast'
import { setCookie, getCookie, getDomain, deleteSessions } from '@/utils/cookie'

export const SessionBus = new Vue()

const AUTH_ERROR_CODES = new Set([1004, 1007, 1008, 1009, 1021])
const ALLOWED_LOGIN_ROLES = new Set([3, 4, 5])
const AUTH_ERROR_MESSAGES = new Set([
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'TOKEN_NOT_FOUND',
  'UNAUTHORIZED',
  'LOGIN_USER_DOUBLE'
])

let forceLogoutPending = false

function t (key, values) {
  return i18n.t(key, values)
}

export function isLoggedIn () {
  return !!getCookie(getDomain() + '-Token')
}

export function getLoginId () {
  return getCookie('LoginId') || ''
}

export function getMemberId () {
  const id = Number(getCookie('UserId'))
  return Number.isFinite(id) && id > 0 ? id : 0
}

export function setSessions (data) {
  if (!data) return
  setCookie('UserId', data.member == null ? 0 : data.member, 1)
  setCookie('LoginId', data.loginid || '', 1)
  setCookie('UserName', data.name || '', 1)
  setCookie('BankName', data.bank_name || '', 1)
  setCookie('BankAccount', data.bank_account || '', 1)
  setCookie('BankMaster', data.bank_master || '', 1)
  setCookie(getDomain() + '-Token', data.token || '', 0.5)
  setCookie('Tel', data.tel || '', 1)
  if (data.token) {
    axios.defaults.headers.Authorization = 'Bearer ' + data.token
  }
}

export function handleLoginResponse (body) {
  if (!body) {
    return { ok: false, message: t('auth.loginFail') }
  }
  if (body.code === 0 && body.data) {
    const role = Number(body.data.role)
    if (!ALLOWED_LOGIN_ROLES.has(role)) {
      return { ok: false, message: t('auth.accountUnavailable') }
    }
  }
  if (body.code === 0) {
    setSessions(body.data)
    return { ok: true, data: body.data }
  }
  if (body.code === 1014) {
    return { ok: false, message: t('auth.wrongCredentials') }
  }
  if (body.code === 1013) {
    return { ok: false, message: t('auth.accountNotApproved') }
  }
  return { ok: false, message: body.message || t('auth.loginFail') }
}

export function clearSession () {
  deleteSessions()
  delete axios.defaults.headers.Authorization
  delete axios.defaults.headers.common.Authorization
  store.commit('SET_LOGGED_IN', false)
}

export function isAuthApiError (body) {
  if (!body || typeof body !== 'object') return false
  if (AUTH_ERROR_CODES.has(body.code)) return true
  const message = String(body.message || '').toUpperCase()
  return AUTH_ERROR_MESSAGES.has(message)
}

export function isNetworkError (error) {
  if (!error) return true
  if (!error.response) return true
  if (error.code === 'ECONNABORTED') return true
  if (String(error.message || '').toLowerCase() === 'network error') return true
  return false
}

export function shouldSkipSessionGuard (config) {
  if (config && config.skipSessionGuard) return true
  const url = String((config && config.url) || '').toLowerCase()
  return url.includes('gateway/login') || url.includes('gateway/signup')
}

export function getAuthErrorMessage (body) {
  const message = String((body && body.message) || '').toUpperCase()
  if (message === 'TOKEN_EXPIRED' || body.code === 1008) {
    return t('auth.sessionExpired')
  }
  if (message === 'TOKEN_INVALID' || body.code === 1009) {
    return t('auth.sessionInvalid')
  }
  if (message === 'TOKEN_NOT_FOUND' || body.code === 1007) {
    return t('auth.sessionNotFound')
  }
  if (message === 'LOGIN_USER_DOUBLE' || body.code === 1021) {
    return t('auth.sessionDoubleLogin')
  }
  return t('auth.sessionExpired')
}

export function forceLogout (options = {}) {
  const wasLoggedIn = isLoggedIn() || store.state.isLoggedIn
  if (!wasLoggedIn && !options.force) return false

  if (forceLogoutPending) return true
  forceLogoutPending = true

  clearSession()
  SessionBus.$emit('session:logout', {
    reason: options.reason || 'auth',
    message: options.message || t('auth.sessionExpired')
  })

  if (options.toast !== false) {
    showToast(options.message || t('auth.sessionExpired'))
  }

  window.setTimeout(() => {
    forceLogoutPending = false
  }, 800)

  return true
}

export function handleApiSessionGuard (body, config, options = {}) {
  if (shouldSkipSessionGuard(config)) return false

  if (isAuthApiError(body)) {
    forceLogout({
      reason: 'auth',
      message: getAuthErrorMessage(body),
      toast: options.toast !== false
    })
    return true
  }

  return false
}

export function handleNetworkSessionGuard (error, config, options = {}) {
  if (shouldSkipSessionGuard(config)) return false
  if (!isLoggedIn()) return false
  if (!isNetworkError(error)) return false

  forceLogout({
    reason: 'network',
    message: options.message || t('auth.sessionNetwork'),
    toast: options.toast !== false
  })
  return true
}
